import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { fetchWeekGames } from "../../utils/weekGames";
import { buildGamesBySlug } from "../../utils/gameStatus";
import {
  dayKey, formatDay, hasPractised, isGameTester, loadPlay, markPractised, nextRun, recordPlay, savePlay, seedDay,
} from "../../games/daily";
import {
  buildChain, dailySeed, describeSpread, GAME_ID, GAME_NAME, gameWeek, isRightCall, pickWeek, PRACTICE_SEED, QUESTIONS, shareText,
} from "../../games/higherLower";
import {
  GameMessage, GameOver, GamePage, Helmet, PracticeTag, PRIMARY_BUTTON, Progress, TimerBar, useQuestionClock,
} from "../../games/GameParts";

// Higher or Lower. The rules and the data live in games/higherLower.js; this
// is the screen. See games/daily.js for the once-a-day rule and the clock.

const LABEL = { fontSize: 10, fontWeight: 800, letterSpacing: "0.07em", textTransform: "uppercase", color: "var(--color-text-faint)", margin: "0 0 5px" };

const matchLine = (t) => `${t.isHome ? "vs" : "at"} ${t.opponentName}`;

function LastTeam({ team }) {
  return (
    <div>
      <p style={LABEL}>Last team</p>
      <div style={{ display: "flex", alignItems: "center", gap: 10, backgroundColor: "var(--color-bg-subtle)", borderRadius: 12, padding: "10px 12px" }}>
        <Helmet team={team} size={40} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--color-text-strong)" }}>{team.name}</div>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {team.result} {matchLine(team)}
          </div>
        </div>
        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1, color: "var(--color-text-strong)", fontVariantNumeric: "tabular-nums" }}>{team.points}</div>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>pts</div>
        </div>
      </div>
    </div>
  );
}

/** The team being called. Only its result until answered; then the rest. */
function NextTeam({ team, outcome }) {
  const tone = outcome?.ok === true ? "success" : outcome?.ok === false ? "danger" : null;
  const spread = describeSpread(team.spread);
  return (
    <div
      style={{
        marginTop: 14, borderRadius: 14, padding: "16px 12px", textAlign: "center",
        border: `2px solid ${tone ? `var(--color-${tone}-border)` : "var(--color-bg-subtle)"}`,
        backgroundColor: tone ? `var(--color-${tone}-bg)` : "transparent",
      }}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}><Helmet team={team} size={76} /></div>
      <div style={{ fontSize: 17, fontWeight: 800, color: "var(--color-text-strong)" }}>{team.name}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color: "var(--color-text-strong)", fontVariantNumeric: "tabular-nums" }}>{team.result}</div>
      {outcome && (
        <>
          <div style={{ fontSize: 16, fontWeight: 800, marginTop: 8, color: "var(--color-text-strong)" }}>{team.points} pts</div>
          <div style={{ fontSize: 13, fontWeight: 800, marginTop: 2, color: `var(--color-${tone}-text)` }}>{outcome.verdict}</div>
          <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--color-border)" }}>
            {matchLine(team)}{spread ? `, ${spread.charAt(0).toLowerCase()}${spread.slice(1)}` : ""}
          </div>
          {team.lines && (
            <div style={{ maxWidth: 230, margin: "6px auto 0", fontSize: 12 }}>
              {team.lines.map((l) => (
                <div key={l.label} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "1px 0", color: "var(--color-text-muted)" }}>
                  <span style={{ textAlign: "left" }}>{l.label}</span>
                  <b style={{ color: "var(--color-text-strong)", fontVariantNumeric: "tabular-nums" }}>{l.points > 0 ? `+${l.points}` : l.points}</b>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CallButtons({ points, onCall }) {
  const button = { ...PRIMARY_BUTTON, display: "flex", alignItems: "center", justifyContent: "center", gap: 6, fontWeight: 800 };
  return (
    <>
      <p style={{ fontSize: 15, fontWeight: 700, textAlign: "center", color: "var(--color-text-strong)", margin: "16px 0 10px" }}>
        Higher or lower than {points}?
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <button type="button" style={button} onClick={() => onCall("higher")}><ArrowUp size={17} strokeWidth={2.6} /> Higher</button>
        <button type="button" style={button} onClick={() => onCall("lower")}><ArrowDown size={17} strokeWidth={2.6} /> Lower</button>
      </div>
    </>
  );
}

/** What the answer says: right, wrong, level, or out of time. */
function judge(previous, next, call) {
  const actual = next.points > previous.points ? "higher" : next.points < previous.points ? "lower" : "level";
  if (!call) return { ok: false, verdict: `Out of time. It was ${actual}.` };
  const ok = isRightCall(previous.points, next.points, call);
  if (actual === "level") return { ok, verdict: "Level. Counts as right." };
  return ok
    ? { ok, verdict: `${actual === "higher" ? "Higher" : "Lower"}. Correct.` }
    : { ok, verdict: `You said ${call}. It was ${actual}.` };
}

function HigherLowerGame() {
  const { teams, seasonConfig, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const done = () => (location.key !== "default" ? navigate(-1) : navigate("/home"));

  // Fixed when the screen opens: a game started before midnight finishes as
  // that day's game.
  const [day] = useState(() => dayKey());

  // A question left on screen when the app was closed counts as out of time,
  // and in this game a miss ends it.
  const [play, setPlay] = useState(() => {
    const saved = loadPlay(GAME_ID, day);
    if (!saved.done && saved.pending !== null && saved.pending === saved.answers.length) {
      const ended = { ...saved, answers: [...saved.answers, false], done: true, pending: null };
      savePlay(GAME_ID, day, ended);
      return ended;
    }
    return saved;
  });

  // Any finished week, a different one most days (higherLower.js). The week
  // is named on screen and in the share text.
  const through = useMemo(() => gameWeek(teams, seasonConfig?.currentWeek), [teams, seasonConfig]);
  const week = useMemo(() => pickWeek(through, seedDay(day, play.run)), [through, day, play.run]);

  // The games for `week`. Held with the week they belong to, so a replay that
  // lands on another week never pairs the old week's games with new points.
  const [loaded, setLoaded] = useState(null);
  const gamesBySlug = loaded && loaded.week === week ? loaded.map : null;
  useEffect(() => {
    if (week === null) return undefined;
    let cancelled = false;
    fetchWeekGames(week, { isFinished: true })
      .then((rows) => { if (!cancelled) setLoaded({ week, map: buildGamesBySlug(rows) }); })
      .catch(() => { if (!cancelled) setLoaded({ week, map: {} }); });
    return () => { cancelled = true; };
  }, [week]);

  const practiceChain = useMemo(() => (gamesBySlug ? buildChain(teams, gamesBySlug, week, PRACTICE_SEED, 2) : null), [teams, gamesBySlug, week]);
  const chain = useMemo(
    () => (gamesBySlug ? buildChain(teams, gamesBySlug, week, dailySeed(seedDay(day, play.run))) : null),
    [teams, gamesBySlug, week, day, play.run],
  );
  const [practising, setPractising] = useState(() => !hasPractised(GAME_ID) && play.answers.length === 0 && !play.done);
  const [phase, setPhase] = useState(play.done ? "over" : "ask");
  const [outcome, setOutcome] = useState(null);

  const ready = Boolean(practising ? practiceChain : chain);
  const asking = ready && phase === "ask";
  // During an answer the question just played is the last one answered.
  const index = phase === "ask" ? play.answers.length : play.answers.length - 1;
  const [previous, next] = practising
    ? (practiceChain || [])
    : chain ? [chain[index], chain[index + 1]] : [];

  // Mark the question as on screen, so closing the app on it counts as a miss.
  useEffect(() => {
    if (!asking || practising) return;
    savePlay(GAME_ID, day, { ...play, pending: play.answers.length });
  }, [asking, practising, day, play]);

  // Report a finished first run of the day. Replays (run > 0) are a tester's
  // own, and repeats are absorbed by the table's unique index.
  useEffect(() => {
    if (practising || !play.done || play.run) return;
    recordPlay(currentUser?.id, GAME_ID, day, play.answers);
  }, [practising, play.done, play.run, play.answers, currentUser, day]);

  const answer = useCallback((call) => {
    if (phase !== "ask" || !previous || !next) return;
    const result = judge(previous, next, call);
    setOutcome(result);
    setPhase("reveal");
    if (practising) return;
    const answers = [...play.answers, result.ok];
    const updated = { ...play, answers, done: !result.ok || answers.length === QUESTIONS, pending: null };
    savePlay(GAME_ID, day, updated);
    setPlay(updated);
  }, [phase, previous, next, practising, play, day]);

  const remaining = useQuestionClock({
    running: asking && !practising,
    questionKey: play.answers.length,
    onExpire: () => answer(null),
  });

  const goOn = () => {
    setOutcome(null);
    if (practising) {
      markPractised(GAME_ID);
      setPractising(false);
      setPhase("ask");
    } else {
      setPhase(play.done ? "over" : "ask");
    }
  };

  const playAgain = () => {
    const fresh = nextRun(play);
    savePlay(GAME_ID, day, fresh);
    setPlay(fresh);
    setOutcome(null);
    setPhase("ask");
  };

  const dayLabel = formatDay(day);
  const subtitle = week !== null ? `Week ${week} results` : null;

  if (week === null) {
    return (
      <GamePage title={GAME_NAME}>
        <GameMessage>Higher or Lower opens once the first week of games is final.</GameMessage>
      </GamePage>
    );
  }

  if (phase === "over") {
    const score = play.answers.filter(Boolean).length;
    return (
      <GamePage title={GAME_NAME} subtitle={`Week ${week} results · ${dayLabel}`}>
        <GameOver
          answers={play.answers}
          total={QUESTIONS}
          text={shareText(play.answers, dayLabel, week)}
          note={`${score === QUESTIONS ? "A perfect game. " : ""}Back tomorrow for a new week of results.`}
          onDone={done}
          onPlayAgain={isGameTester(currentUser?.id) ? playAgain : null}
        />
      </GamePage>
    );
  }

  if (!gamesBySlug) {
    return <GamePage title={GAME_NAME} subtitle={subtitle}><GameMessage>Loading week {week} results.</GameMessage></GamePage>;
  }
  if (!previous || !next) {
    return <GamePage title={GAME_NAME} subtitle={subtitle}><GameMessage>Week {week} results could not be loaded. Try again shortly.</GameMessage></GamePage>;
  }

  return (
    <GamePage title={GAME_NAME} subtitle={subtitle}>
      {practising ? (
        <>
          <PracticeTag />
          <p style={{ fontSize: 13, lineHeight: 1.5, color: "var(--color-text-strong)", margin: "0 0 12px" }}>
            Every team scored fantasy points in week {week}. Call whether the next team scored <b>higher or lower</b> than
            the last. You get 10 seconds a call, and one wrong call ends the game. Get to <b>10</b>.
          </p>
        </>
      ) : (
        <>
          <Progress answers={play.answers} total={QUESTIONS} current={index} />
          <TimerBar remaining={remaining} stopped={phase !== "ask"} />
        </>
      )}

      <LastTeam team={previous} />
      <NextTeam team={next} outcome={phase === "reveal" ? outcome : null} />

      {phase === "ask" ? (
        <CallButtons points={previous.points} onCall={answer} />
      ) : (
        <button type="button" onClick={goOn} style={{ ...PRIMARY_BUTTON, marginTop: 14 }}>
          {practising ? "Start today's game" : play.done ? "See your score" : "Next"}
        </button>
      )}
    </GamePage>
  );
}

export default HigherLowerGame;
